// Schedule math — NDS Bonus Management Module.
// End time is NEVER stored; it is always derived here (spec §BR-01):
//   total       = startMinutes + durationMinutes
//   dayOffset   = floor(total / 1440)
//   endMinutes  = total mod 1440
//   endDayIndex = (startDayIndex + dayOffset) mod 7   (0 = Monday … 6 = Sunday)

import { DAYS_FULL, DAYS_ABBR } from './tokens';

export const pad = (n) => String(n).padStart(2, '0');

export const uid = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// "HH:mm" or "HH:mm:ss" -> minutes since midnight. Null-safe.
export function parseHHMM(str) {
  if (!str) return null;
  const [h, m] = String(str).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function minutesToHHMM(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${pad(h)}:${pad(m)}`;
}

// 12-hour display, e.g. 1140 -> "7:00 PM".
export function formatTime(mins) {
  const total = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = total % 60;
  const ampm = h < 12 ? 'AM' : 'PM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${pad(m)} ${ampm}`;
}

export function formatDuration(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${pad(m)}m`;
}

// BR-01
export function computeEnd(dayIndex, startMin, durationMin) {
  const total = startMin + durationMin;
  const offset = Math.floor(total / 1440);
  return {
    endDay: (((dayIndex + offset) % 7) + 7) % 7,
    endMin: ((total % 1440) + 1440) % 1440,
    nextDay: offset > 0,
  };
}

export function crossesMidnight(startMin, durationMin) {
  return startMin + durationMin > 1440;
}

// Position (0–100) of the midnight tick along the span bar (spec §4.4).
export function midnightPercent(startMin, durationMin) {
  if (!crossesMidnight(startMin, durationMin)) return null;
  return ((1440 - startMin) / durationMin) * 100;
}

// One human-readable schedule line per selected day of a block.
// e.g. "Monday 7:00 PM → Tue 7:00 AM" or "Monday — All day".
export function blockLines(block) {
  const days = (block.days || []).slice().sort((a, b) => a - b);
  if (block.allDay) {
    return days.map((d) => `${DAYS_FULL[d]} — All day`);
  }
  const startMin = parseHHMM(block.startTime);
  const dur = block.durationMinutes;
  if (startMin == null || dur == null) return [];
  return days.map((d) => {
    const { endDay, endMin, nextDay } = computeEnd(d, startMin, dur);
    const end = nextDay
      ? `${DAYS_ABBR[endDay]} ${formatTime(endMin)}`
      : formatTime(endMin);
    return `${DAYS_FULL[d]} ${formatTime(startMin)} → ${end}`;
  });
}

// Meta-line summary for the list row: "All day" or "N blocks".
export function scheduleSummary(bonus) {
  const blocks = bonus.blocks || [];
  if (blocks.length === 0) return 'No schedule';
  if (blocks.some((b) => b.allDay) && blocks.every((b) => b.allDay))
    return 'All day';
  return `${blocks.length} block${blocks.length > 1 ? 's' : ''}`;
}

// Non-blocking overlap warnings (BR-06): two blocks of the same bonus that
// cover the same day at overlapping clock windows. Returns human strings.
export function overlapWarnings(blocks) {
  const warnings = [];
  const windows = blocks.map((b, i) => ({ i, ...b }));
  for (let a = 0; a < windows.length; a += 1) {
    for (let b = a + 1; b < windows.length; b += 1) {
      const A = windows[a];
      const B = windows[b];
      const shared = (A.days || []).filter((d) => (B.days || []).includes(d));
      if (shared.length === 0) continue;
      const overlapDay = shared.find((d) => dayOverlaps(A, B, d));
      if (overlapDay != null) {
        warnings.push(
          `Block ${a + 1} and Block ${b + 1} overlap on ${DAYS_FULL[overlapDay]}.`
        );
      }
    }
  }
  return warnings;
}

function dayOverlaps(A, B, day) {
  if (A.allDay || B.allDay) return true;
  const as = parseHHMM(A.startTime);
  const bs = parseHHMM(B.startTime);
  if (as == null || bs == null) return false;
  const ae = as + A.durationMinutes;
  const be = bs + B.durationMinutes;
  // Both windows anchored to the same start day; compare absolute minutes.
  return as < be && bs < ae;
}
